import { useState } from "react";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import CustomInput from "../../../common/CustomInput";
import CustomTextArea from "../../../common/CustomTextArea";
import SuccessModal from "../../../common/SuccessModal";
import BackButton from "../../../common/BackButton";
import { handleSmartBack } from "../../../../utils/navigationHelper";
import { useSendNotificationOrganization } from "../../../../hooks/organization/notification/useSendNotificationOrganization";
import SubSchoolSelector from "./SubSchoolSelector";
import FileUploader from "./FileUploader";
import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import { Button } from "react-bootstrap";

const CreateNotification = () => {
    const navigate = useNavigate();
    const { loading, error, sendNotification } =
        useSendNotificationOrganization();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);
    const [schoolError, setSchoolError] = useState("");

    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
    const MAX_FILES = 5;

    const sendNotificationSchema = yup.object().shape({
        title: yup
            .string()
            .min(3, "Title must be at least 3 characters long")
            .max(100, "Title must not exceed 100 characters")
            .required("Title is required"),
        description: yup
            .string()
            .min(10, "Description must be at least 10 characters long")
            .max(500, "Description must not exceed 500 characters")
            .required("Description is required"),
        media_files: yup
            .array()
            .max(MAX_FILES, `You can upload a maximum of ${MAX_FILES} files`)
            .test("fileSize", "Each file must be less than 2MB", (files) => {
                if (!files || files.length === 0) return true;
                return files.every((file) => file.size <= MAX_FILE_SIZE);
            }),
    });

    const onSubmit = async (values, actions) => {
        if (selectedSchoolIds.length === 0) {
            setSchoolError("Please select at least one school");
            return;
        }
        setSchoolError("");

        await sendNotification(
            {
                title: values.title,
                description: values.description,
                sub_school_ids: selectedSchoolIds,
                role_slug: ["school-admin"],
                media_files: values.media_files,
            },
            () => {
                setShowSuccessModal(true);
                actions.resetForm();
            },
        );
    };

    const handleSendAnother = () => {
        setShowSuccessModal(false);
    };

    const handleGoBack = () => {
        setShowSuccessModal(false);
        handleSmartBack(navigate, "/organization/notification");
    };

    return (
        <div className="classroom-cnt">
            <div className="pb-2">
                <BackButton />
            </div>
            <div className="classroom-container">
                <img src={classRoomImg} className="img" alt="Notification Centre" />
                <h2 className="text">Send Notification</h2>
            </div>
            <div className="custom-card mt-4">
                <Formik
                    initialValues={{ title: "", description: "", media_files: [] }}
                    validationSchema={sendNotificationSchema}
                    onSubmit={onSubmit}
                >
                    {({ isSubmitting, setFieldValue, values, errors, touched }) => (
                        <Form className="d-flex flex-column gap-4">
                            <CustomInput
                                label="Title"
                                name="title"
                                type="text"
                                placeholder="Enter notification title"
                                className="custom-primary-input"
                                layout="stack"
                                labelWidth={{
                                    mobile: "100px",
                                    tablet: "100px",
                                    desktop: "100px",
                                }}
                            />
                            <CustomTextArea
                                label="Description"
                                height="200px"
                                name="description"
                                type="text"
                                placeholder="Enter notification description"
                                className="custom-primary-input"
                                layout="stack"
                                labelWidth={{
                                    mobile: "10px",
                                    tablet: "20px",
                                    desktop: "100px",
                                }}
                            />

                            <SubSchoolSelector
                                selectedIds={selectedSchoolIds}
                                onChange={(ids) => {
                                    setSelectedSchoolIds(ids);
                                    if (ids.length > 0) setSchoolError("");
                                }}
                                disabled={loading}
                            />
                            {schoolError && (
                                <div className="custom-error">{schoolError}</div>
                            )}

                            <FileUploader
                                files={values.media_files}
                                onChange={(files) => setFieldValue("media_files", files)}
                                disabled={loading}
                                maxFiles={MAX_FILES}
                                maxSizeMB={2}
                            />
                            {touched.media_files && errors.media_files && (
                                <div className="custom-error">{errors.media_files}</div>
                            )}

                            {error && <div className="custom-error">{error}</div>}

                            <Button
                                disabled={isSubmitting || loading}
                                type="submit"
                                className="custom-primary-button"
                            >
                                {loading ? "Sending..." : "Send Notification"}
                            </Button>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* Success Modal */}
            <SuccessModal
                showModal={showSuccessModal}
                handleClose={() => setShowSuccessModal(false)}
                title="Notification Sent"
                message="Your notification has been sent successfully!"
                primaryButtonText="Send Another"
                secondaryButtonText="Go Back"
                onPrimaryClick={handleSendAnother}
                onSecondaryClick={handleGoBack}
            />
        </div>
    );
};

export default CreateNotification;
